import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'

/**
 * What a part, category or footprint says about its EDA representation.
 *
 * Mirrors the backend's `EdaInfo`. Every field is optional on purpose: an unset
 * field means "ask whatever is above me", which is what makes it worth setting a
 * symbol once on a category instead of on two hundred resistors.
 */
export interface EdaInfoValue {
  symbol?: string | null
  footprint?: string | null
  reference_prefix?: string | null
  value?: string | null
  exclude_from_bom?: boolean | null
  exclude_from_board?: boolean | null
  exclude_from_sim?: boolean | null
  visibility?: Record<string, boolean>
}

/** Where a value would come from if this one were left empty. */
export interface EdaInherited {
  symbol?: string | null
  footprint?: string | null
  reference_prefix?: string | null
}

type TextKey = 'symbol' | 'footprint' | 'reference_prefix' | 'value'
type FlagKey = 'exclude_from_bom' | 'exclude_from_board' | 'exclude_from_sim'

const TEXT_FIELDS: { key: TextKey; label: string; placeholder: string; hint?: string }[] = [
  { key: 'symbol', label: 'Symbol', placeholder: 'Device:R' },
  { key: 'footprint', label: 'Footprint', placeholder: 'Resistor_SMD:R_0805_2012Metric' },
  { key: 'reference_prefix', label: 'Reference prefix', placeholder: 'R' },
  {
    key: 'value',
    label: 'Value',
    placeholder: '10k',
    hint: 'Left empty, the schematic shows the part name.',
  },
]

const FLAGS: { key: FlagKey; label: string }[] = [
  { key: 'exclude_from_bom', label: 'Exclude from the BOM' },
  { key: 'exclude_from_board', label: 'Exclude from the board' },
  { key: 'exclude_from_sim', label: 'Exclude from simulation' },
]

/**
 * Editor for one `eda_info` object.
 *
 * `inherited` is what the backend would resolve if every field here were left
 * empty; it is shown as the placeholder so the person editing can see what they
 * are overriding rather than guessing.
 */
export function EdaInfoFields({
  value,
  onChange,
  inherited,
  idPrefix = 'eda',
}: {
  value: EdaInfoValue
  onChange: (next: EdaInfoValue) => void
  inherited?: EdaInherited
  idPrefix?: string
}) {
  const setText = (key: TextKey, text: string) => {
    const next = { ...value }
    if (text.trim()) next[key] = text
    // An empty box means "inherit", which is an absent key, not an empty string.
    else delete next[key]
    onChange(next)
  }

  const cycleFlag = (key: FlagKey) => {
    const next = { ...value }
    // Three states: inherit → yes → no → inherit. A checkbox that could only
    // say yes or no would make "inherit" unreachable once touched.
    const current = next[key]
    if (current === undefined || current === null) next[key] = true
    else if (current === true) next[key] = false
    else delete next[key]
    onChange(next)
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {TEXT_FIELDS.map((field) => {
        const id = `${idPrefix}-${field.key}`
        const from = inherited?.[field.key as keyof EdaInherited]
        return (
          <div className="space-y-1.5" key={field.key}>
            <Label htmlFor={id}>{field.label}</Label>
            <Input
              id={id}
              value={value[field.key] ?? ''}
              placeholder={from ? `${from} (inherited)` : field.placeholder}
              onChange={(event) => setText(field.key, event.target.value)}
            />
            {field.hint && <p className="text-muted-foreground text-xs">{field.hint}</p>}
          </div>
        )
      })}

      <fieldset className="grid gap-2 sm:col-span-2">
        <legend className="sr-only">Exclusions</legend>
        {FLAGS.map((flag) => {
          const state = value[flag.key]
          const set = state === true || state === false
          return (
            <label className="flex items-center gap-2 text-sm" key={flag.key}>
              <Checkbox
                checked={state === true}
                aria-label={flag.label}
                onCheckedChange={() => cycleFlag(flag.key)}
              />
              {flag.label}
              <span className="text-muted-foreground text-xs">
                {set ? (state === true ? 'yes' : 'no') : 'inherited'}
              </span>
            </label>
          )
        })}
      </fieldset>
    </div>
  )
}

/** Read an `eda_info` column, which the API types as `unknown`. */
export function readEdaInfo(value: unknown): EdaInfoValue {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as EdaInfoValue) : {}
}
